import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMe } from '../../../store';
import { useBundleCheck } from '../../../utils/useBundleCheck';
import {
    BundleIssue,
    BundleReport,
    BundleSkuItem,
    MaterialBundlePayload,
    submitMaterialBundle,
} from '../../../api/bundle';

// 材料创建向导的状态层 (2026-09-16, 设计 doc/relate-import/07).
//
// 一条主线: **向导状态 = 一份材料套件载荷**. 每步"下一步"拿当前载荷去服务端跑 dryRun,
// 服务端怎么判前端就怎么显示 —— 前端不复刻任何校验规则 (类别组合/字典值/理化组适用性/
// 参数模板值域全在后端), 只负责把回来的 issue 按 path 贴到对应步骤的字段旁边.
//
// 为什么不本地校验: 这些规则的真值在库里 (类别树/字典/模板/设备), 前端抄一份必然漂移;
// 而且提交时服务端还要再跑一遍, 两处不一致时用户会看到"页面说行, 提交说不行".

/** 向导步骤 (① 必填, ②③④ 可选, ⑤ 提交). */
export const STEPS = [
    { key: 'basic', label: '基本信息', optional: false },
    { key: 'phys', label: '理化参数', optional: true },
    { key: 'relations', label: '关联数据', optional: true },
    { key: 'params', label: '加工参数', optional: true },
    { key: 'review', label: '预检与提交', optional: false },
] as const;

export type StepKey = (typeof STEPS)[number]['key'];

/** stepOf issue 的 path → 归属步骤. 与后端报告的 path 前缀一一对应 (设计 07 B3). */
export function stepOf(path: string): StepKey {
    if (path.startsWith('physValues') || path.startsWith('newDicts.physSchemas'))
        return 'phys';
    if (
        path.startsWith('skus') ||
        path.startsWith('capabilities') ||
        path.startsWith('tips')
    ) {
        return 'relations';
    }
    if (path.startsWith('params')) return 'params';
    return 'basic'; // material.* 与结构层错误 (解析/未知键) 都回第一步
}

/** 载荷里被 dryRun 判过的字段路径 → 该字段的 issue (最严重的一条). */
export type IssueByPath = Record<string, BundleIssue>;

const DRAFT_KEY_BASE = 'material-wizard-draft-v1';

/**
 * draftKeyOf 草稿按登录用户分开存 (2026-09-21).
 * 草稿只在浏览器本地, key 原先是固定的 —— 共享工位/演示机上 A 退出、B 登录,
 * B 一进向导就被 A 的半张表单"恢复"出来。带上 username 后各人各一份;
 * 拿不到用户名 (me 还没加载完) 时不读也不写, 宁可不恢复也不串号。
 */
export function draftKeyOf(username: string): string {
    return username ? `${DRAFT_KEY_BASE}:${username}` : '';
}
const DRAFT_DEBOUNCE_MS = 1000;

/** 空载荷: 只有必填的两个类别位, 其余段按"没填就不发"处理. */
export function emptyPayload(): MaterialBundlePayload {
    return { material: { type1: '', type2: '' } };
}

interface DraftEnvelope {
    payload: MaterialBundlePayload;
    step: StepKey;
    savedAt: number;
}

/** loadDraft 读浏览器里的未完成草稿 (localStorage 不可用时静默当作没有). */
export function loadDraft(username: string): DraftEnvelope | null {
    const key = draftKeyOf(username);
    if (!key) return null;
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return null;
        const d = JSON.parse(raw) as DraftEnvelope;
        if (!d?.payload?.material) return null;
        return d;
    } catch {
        return null;
    }
}

export function clearDraft(username: string): void {
    const key = draftKeyOf(username);
    if (!key) return;
    try {
        localStorage.removeItem(key);
    } catch {
        /* 隐私模式 / 站点数据被禁: 草稿只是便利功能, 丢了不影响主流程 */
    }
}

/** dropLegacyDraft 清掉 2026-09-21 之前那份不带用户名的旧草稿: 它不再被读取,
 * 留着只会在共享浏览器里继续躺着别人的填写内容. */
function dropLegacyDraft(): void {
    try {
        localStorage.removeItem(DRAFT_KEY_BASE);
    } catch {
        /* 隐私模式 / 站点数据被禁 */
    }
}

/** hasContent 载荷里是否已经填了东西 (决定要不要拦离开、要不要提示恢复草稿). */
export function hasContent(p: MaterialBundlePayload): boolean {
    const m = p.material;
    return !!(
        m.type1 ||
        m.type2 ||
        m.nameZh ||
        m.nameEn ||
        m.xcode ||
        p.skus?.length ||
        p.capabilities?.length ||
        p.params?.length ||
        (p.physValues && Object.keys(p.physValues).length) ||
        p.tips?.tipCodes?.length ||
        p.newDicts?.physSchemas?.length
    );
}

/**
 * toRequest 把向导草稿归一成提交载荷:
 * - 空字符串/空数组/空对象一律不发 (后端对"填了但是空"和"没填"判得不一样, 如 xcode 留空=自动取号);
 * - 关联的已有 SKU 只发 skuCode (本体字段以库中为准, 发了也会被忽略并告警).
 */
export function toRequest(p: MaterialBundlePayload): MaterialBundlePayload {
    const m = p.material;
    const material: Record<string, unknown> = {};
    Object.entries(m).forEach(([k, v]) => {
        if (v === '' || v === undefined || v === null) return;
        if (Array.isArray(v) && v.length === 0) return;
        material[k] = v;
    });
    const out: MaterialBundlePayload = {
        material: material as unknown as MaterialBundlePayload['material'],
    };
    if (p.skus?.length) {
        out.skus = p.skus.map((s) => {
            if (s.linked) return { skuCode: s.skuCode };
            const item: Record<string, unknown> = {};
            Object.entries(s).forEach(([k, v]) => {
                if (k === 'linked' || v === '' || v === undefined || v === null) return;
                item[k] = v;
            });
            return item as unknown as BundleSkuItem;
        });
    }
    if (p.capabilities?.length) out.capabilities = p.capabilities;
    if (p.physValues) {
        const groups: MaterialBundlePayload['physValues'] = {};
        Object.entries(p.physValues).forEach(([g, vals]) => {
            const kept: Record<string, unknown> = {};
            Object.entries(vals || {}).forEach(([k, v]) => {
                if (v === '' || v === undefined || v === null) return;
                kept[k] = v;
            });
            if (Object.keys(kept).length) groups[g] = kept;
        });
        if (Object.keys(groups).length) out.physValues = groups;
    }
    if (p.tips?.tipCodes?.length || p.tips?.includeRecommended) out.tips = p.tips;
    if (p.params?.length) out.params = p.params;
    if (p.newDicts?.physSchemas?.length)
        out.newDicts = { physSchemas: p.newDicts.physSchemas };
    return out;
}

export interface UseBundleDraft {
    payload: MaterialBundlePayload;
    /** patch 只改材料段的若干字段 (其余段用 setPayload) */
    patchMaterial: (patch: Partial<MaterialBundlePayload['material']>) => void;
    setPayload: (next: MaterialBundlePayload) => void;
    step: StepKey;
    goto: (s: StepKey) => void;
    /** 展示用报告 (已撤掉改过的那几条); 右栏与字段红字都用它 */
    report: BundleReport | null;
    /** 服务端最后一次原始判定: 能不能提交以它为准 */
    rawReport: BundleReport | null;
    /** 载荷在上次预检之后改过: 报告已过期, 不可作提交依据 (提交页会自动重跑) */
    stale: boolean;
    checking: boolean;
    error: string;
    /** runCheck 跑一次 dryRun; 返回是否无 error (供"下一步"决定放不放行) */
    runCheck: () => Promise<boolean>;
    /** 收下提交接口返回的报告 (提交被拒时以服务端那次判定为准) */
    adoptReport: (rep: BundleReport) => void;
    issuesByStep: Record<StepKey, BundleIssue[]>;
    issueByPath: IssueByPath;
    restoredAt: number;
    dismissRestored: () => void;
}

/** severityRank 同一路径多条 issue 时, 展示最严重的那条. */
const severityRank: Record<string, number> = { error: 3, warning: 2, info: 1 };

export function useBundleDraft(initial?: MaterialBundlePayload): UseBundleDraft {
    const [payload, setPayloadRaw] = useState<MaterialBundlePayload>(
        () => initial || emptyPayload(),
    );
    const [step, setStep] = useState<StepKey>('basic');
    const [restoredAt, setRestoredAt] = useState(0);
    // payloadRef 让 beforeunload 读到最新载荷 (避免闭包捕获旧值)
    const payloadRef = useRef(payload);
    payloadRef.current = payload;
    // 草稿按登录用户分开存
    const { username } = useMe();
    const draftKey = draftKeyOf(username);
    const restoredRef = useRef(false);

    // 有草稿就恢复 (initial 非空时是克隆/文件回填, 优先级更高);
    // 等 me 加载出用户名再读 —— 草稿按登录用户分开存, 拿不到用户名时不恢复也不落盘
    useEffect(() => {
        if (initial || !draftKey || restoredRef.current) return;
        restoredRef.current = true;
        dropLegacyDraft();
        const d = loadDraft(username);
        if (d && hasContent(d.payload)) {
            setPayloadRaw(d.payload);
            setStep(d.step);
            setRestoredAt(d.savedAt);
        }
        // initial 只在首次挂载有意义, 不进依赖
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [draftKey, username]);

    // 自动存草稿 (防抖): 用户中途去维护工艺类型/新建贴士再回来, 填的内容还在
    useEffect(() => {
        if (!draftKey || !hasContent(payload)) return;
        const t = window.setTimeout(() => {
            try {
                localStorage.setItem(
                    draftKey,
                    JSON.stringify({ payload, step, savedAt: Date.now() }),
                );
            } catch {
                /* 存不下就算了, 不打断填写 */
            }
        }, DRAFT_DEBOUNCE_MS);
        return () => window.clearTimeout(t);
    }, [payload, step, draftKey]);

    // 关页面前拦一下 (只在填了东西时)
    useEffect(() => {
        const onBeforeUnload = (e: BeforeUnloadEvent) => {
            if (!hasContent(payloadRef.current)) return;
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, []);

    const setPayload = useCallback((next: MaterialBundlePayload) => {
        setPayloadRaw(next);
    }, []);

    const patchMaterial = useCallback(
        (patch: Partial<MaterialBundlePayload['material']>) => {
            setPayloadRaw((cur) => ({ ...cur, material: { ...cur.material, ...patch } }));
        },
        [],
    );

    // 预检状态 (报告/过期判定/提交页自动重跑) 与设备向导共用, 见 utils/useBundleCheck
    const check = useBundleCheck(payload, toRequest, submitMaterialBundle, step === 'review');
    const shownReport = check.report;

    const issuesByStep = useMemo(() => {
        const out: Record<StepKey, BundleIssue[]> = {
            basic: [],
            phys: [],
            relations: [],
            params: [],
            review: [],
        };
        if (!shownReport) return out;
        [...shownReport.errors, ...shownReport.warnings, ...shownReport.infos].forEach((i) => {
            out[stepOf(i.path)].push(i);
        });
        return out;
    }, [shownReport]);

    const issueByPath = useMemo(() => {
        const out: IssueByPath = {};
        if (!shownReport) return out;
        [...shownReport.errors, ...shownReport.warnings, ...shownReport.infos].forEach((i) => {
            const cur = out[i.path];
            if (!cur || severityRank[i.severity] > severityRank[cur.severity]) {
                out[i.path] = i;
            }
        });
        return out;
    }, [shownReport]);

    return {
        payload,
        patchMaterial,
        setPayload,
        step,
        goto: setStep,
        report: shownReport,
        rawReport: check.rawReport,
        stale: check.stale,
        checking: check.checking,
        error: check.error,
        runCheck: check.runCheck,
        adoptReport: check.adoptReport,
        issuesByStep,
        issueByPath,
        restoredAt,
        dismissRestored: () => setRestoredAt(0),
    };
}
